/**
 * E2E-04E: Event Operations MDM Setup
 * =====================================
 * MDM-STD-01 compliant admin panel for all 9 event operational config types.
 * Route: /event-ops-mdm
 *
 * Config types:
 *   THREAD_TEMPLATE · TASK_TEMPLATE · EXCEPTION_CATEGORY · CHECKLIST_TEMPLATE
 *   ESCALATION_RULE · DELIVERY_CRITERIA · ACCEPTANCE_CRITERIA
 *   MILESTONE_TEMPLATE · THREAD_ACTIVATION_POLICY
 *
 * Capabilities:
 *   event.ops.manage — create / edit / activate / deactivate
 *   event.ops.read   — list / detail / history (read-only)
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { opsSetupApi, formatError } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Plus, RefreshCw, X, Edit3, History,
  ToggleLeft, ToggleRight, ChevronRight, AlertTriangle,
  CheckCircle2, Shield, Layers, Clock, ListChecks,
  Zap, AlertCircle, Target, GitMerge, Activity, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// ── Config type definitions ───────────────────────────────────────────────────

const CONFIG_TYPES = [
  {
    id: "THREAD_TEMPLATE",
    label: "Thread Templates",
    icon: Layers,
    description: "Define EventThread types and their automatic activation triggers",
    color: { bg: "#EEF4FF", text: "#0176D3" },
  },
  {
    id: "TASK_TEMPLATE",
    label: "Task Templates",
    icon: ListChecks,
    description: "Standard recurring operational task definitions per lifecycle phase",
    color: { bg: "#F0FDF4", text: "#166534" },
  },
  {
    id: "EXCEPTION_CATEGORY",
    label: "Exception Categories",
    icon: AlertTriangle,
    description: "Classification codes for operational incidents and exceptions",
    color: { bg: "#FEF2F2", text: "#DC2626" },
  },
  {
    id: "CHECKLIST_TEMPLATE",
    label: "Checklist Templates",
    icon: CheckCircle2,
    description: "Pre/during/post event checklist templates with standard line items",
    color: { bg: "#F0FDF4", text: "#166534" },
  },
  {
    id: "ESCALATION_RULE",
    label: "Escalation Rules",
    icon: Zap,
    description: "Automated escalation thresholds and role-based notification policies",
    color: { bg: "#FFFBEB", text: "#D97706" },
  },
  {
    id: "DELIVERY_CRITERIA",
    label: "Delivery Criteria",
    icon: Target,
    description: "Supplier delivery acceptance quality criteria and evidence requirements",
    color: { bg: "#F5F3FF", text: "#7C3AED" },
  },
  {
    id: "ACCEPTANCE_CRITERIA",
    label: "Acceptance Criteria",
    icon: Shield,
    description: "Standard service acceptance criteria for AP delivery review",
    color: { bg: "#EFF6FF", text: "#1D4ED8" },
  },
  {
    id: "MILESTONE_TEMPLATE",
    label: "Milestone Templates",
    icon: Activity,
    description: "Standard event lifecycle milestone templates for planning and tracking",
    color: { bg: "#FFF7F0", text: "#EA580C" },
  },
  {
    id: "THREAD_ACTIVATION_POLICY",
    label: "Activation Policies",
    icon: GitMerge,
    description: "Rules governing when and how EventThreads are automatically activated",
    color: { bg: "#F0FDF4", text: "#166534" },
  },
];

const EMPTY_FORM = {
  item_code: "",
  display_name: "",
  description: "",
  sort_order: "99",
  effective_from: "",
  effective_to: "",
  thread_type: "",
  visibility_class: "",
  is_default: false,
  metadata_raw: "{}",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const ct = CONFIG_TYPES.find(c => c.id === type);
  const color = ct?.color || { bg: "#F3F4F6", text: "#374151" };
  return (
    <span style={{ background: color.bg, color: color.text, padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700 }}>
      {ct?.label || type}
    </span>
  );
}

function StatusPill({ active }) {
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700,
      background: active ? "#F0FDF4" : "#F9FAFB",
      color: active ? "#166534" : "#9CA3AF",
      border: `1px solid ${active ? "#BBF7D0" : "#E5E7EB"}`,
    }}>
      {active ? "ACTIVE" : "INACTIVE"}
    </span>
  );
}

function PlatformBadge() {
  return (
    <span style={{ background: "#F0F7FF", color: "#1A56DB", padding: "2px 7px", borderRadius: 6, fontSize: 10, fontWeight: 700, border: "1px solid #B4D3FE" }}>
      PLATFORM
    </span>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function EventOpsMDMSetup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const caps = user?.capabilities || [];
  const canView   = caps.includes("event.ops.read") || caps.includes("event.ops.manage");
  const canManage = caps.includes("event.ops.manage");

  const [selectedType,    setSelectedType]    = useState("THREAD_TEMPLATE");
  const [items,           setItems]           = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [search,          setSearch]          = useState("");
  const [showInactive,    setShowInactive]    = useState(false);
  const [totalByType,     setTotalByType]     = useState({});

  const [detailItem,      setDetailItem]      = useState(null);
  const [historyData,     setHistoryData]     = useState(null);
  const [historyLoading,  setHistoryLoading]  = useState(false);
  const [showCreate,      setShowCreate]      = useState(false);
  const [createForm,      setCreateForm]      = useState(EMPTY_FORM);
  const [editItem,        setEditItem]        = useState(null);
  const [editForm,        setEditForm]        = useState(null);
  const [saving,          setSaving]          = useState(false);
  const [toggling,        setToggling]        = useState(null);

  const handleExport = () => {
    const exportRows = [
      ["item_code", "display_name", "description", "sort_order", "tenant_id", "effective_from", "effective_to", "status", "thread_type", "visibility_class", "is_default", "created_by", "created_at"],
      ...filteredItems.map(i => [
        i.item_code, i.display_name, i.description || "",
        i.sort_order, i.tenant_id, i.effective_from || "", i.effective_to || "",
        i.status, i.thread_type || "", i.visibility_class || "",
        i.is_default ? "true" : "false",
        i.created_by || "", (i.created_at || "").slice(0, 10),
      ]),
    ];
    const csv = exportRows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ops_mdm_${selectedType.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredItems.length} ${selectedTypeInfo?.label} records`);
  };

  const loadItems = useCallback(async (type = selectedType) => {
    setLoading(true);
    try {
      const res = await opsSetupApi.listConfig(type, { include_inactive: showInactive });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(formatError(e) || "Failed to load config items");
    } finally {
      setLoading(false);
    }
  }, [selectedType, showInactive]);

  // Load counts for all types on mount
  useEffect(() => {
    Promise.all(CONFIG_TYPES.map(ct =>
      opsSetupApi.listConfig(ct.id).then(r => ({ id: ct.id, count: r.data.total || 0 })).catch(() => ({ id: ct.id, count: 0 }))
    )).then(results => {
      const totals = {};
      results.forEach(r => { totals[r.id] = r.count; });
      setTotalByType(totals);
    });
  }, []);

  useEffect(() => { loadItems(selectedType); }, [selectedType, showInactive]);

  const filteredItems = search
    ? items.filter(i => (i.item_code + i.display_name + (i.description || "")).toLowerCase().includes(search.toLowerCase()))
    : items;

  const handleCreate = async () => {
    setSaving(true);
    try {
      let meta = {};
      try { meta = JSON.parse(createForm.metadata_raw || "{}"); } catch { meta = {}; }
      await opsSetupApi.createConfig(selectedType, {
        item_code:         createForm.item_code.toUpperCase(),
        display_name:      createForm.display_name,
        description:       createForm.description,
        sort_order:        parseInt(createForm.sort_order || "99"),
        effective_from:    createForm.effective_from || undefined,
        effective_to:      createForm.effective_to || undefined,
        thread_type:       createForm.thread_type || undefined,
        visibility_class:  createForm.visibility_class || undefined,
        is_default:        createForm.is_default,
        metadata:          meta,
      });
      toast.success("Configuration item created");
      setShowCreate(false);
      setCreateForm(EMPTY_FORM);
      loadItems();
    } catch (e) {
      toast.error(formatError(e) || "Failed to create item");
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      let meta = {};
      try { meta = JSON.parse(editForm.metadata_raw || "{}"); } catch { meta = {}; }
      await opsSetupApi.updateConfig(selectedType, editItem.id, {
        display_name:      editForm.display_name,
        description:       editForm.description,
        sort_order:        parseInt(editForm.sort_order || "99"),
        effective_from:    editForm.effective_from || undefined,
        effective_to:      editForm.effective_to || undefined,
        visibility_class:  editForm.visibility_class || undefined,
        is_default:        editForm.is_default,
        metadata:          meta,
      });
      toast.success("Configuration item updated");
      setEditItem(null);
      loadItems();
    } catch (e) {
      toast.error(formatError(e) || "Failed to update item");
    } finally { setSaving(false); }
  };

  const handleToggle = async (item) => {
    setToggling(item.id);
    try {
      if (item.status === "ACTIVE") {
        await opsSetupApi.deactivateConfig(selectedType, item.id);
        toast.success(`${item.display_name} deactivated`);
      } else {
        await opsSetupApi.activateConfig(selectedType, item.id);
        toast.success(`${item.display_name} activated`);
      }
      loadItems();
    } catch (e) {
      toast.error(formatError(e) || "Failed to toggle status");
    } finally { setToggling(null); }
  };

  const loadHistory = async (item) => {
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const res = await opsSetupApi.getConfigHistory(selectedType, item.id);
      setHistoryData(res.data);
    } catch (e) {
      toast.error("Failed to load history");
    } finally { setHistoryLoading(false); }
  };

  if (!canView) return (
    <div className="p-8 text-center text-slate-400">You do not have access to Event Operations MDM.</div>
  );

  const selectedTypeInfo = CONFIG_TYPES.find(c => c.id === selectedType);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/setup-studio")} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="back-to-setup-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#181818]">Event Operations MDM</h1>
              <p className="text-xs text-[#706E6B] mt-0.5">MDM-STD-01 · Operational configuration reference data</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 flex gap-6">
        {/* Left sidebar — type selector */}
        <div className="w-56 flex-shrink-0">
          <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-[#E5E5E5]">
              <p className="text-xs font-mono text-[#706E6B] uppercase tracking-wider">Config Types</p>
            </div>
            {CONFIG_TYPES.map(ct => {
              const Icon = ct.icon;
              const active = ct.id === selectedType;
              return (
                <button
                  key={ct.id}
                  onClick={() => { setSelectedType(ct.id); setDetailItem(null); setHistoryData(null); }}
                  data-testid={`ops-mdm-type-${ct.id}`}
                  className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-all border-b border-[#F3F3F3] last:border-0 ${
                    active ? "bg-[#EEF4FF] text-[#0176D3] font-semibold" : "text-[#444444] hover:bg-[#F8FAFC]"
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-[#0176D3]" : "text-[#706E6B]"}`} />
                    <span className="truncate text-xs">{ct.label}</span>
                  </div>
                  {totalByType[ct.id] > 0 && (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${active ? "bg-[#0176D3]/10 text-[#0176D3]" : "bg-[#E5E5E5] text-[#706E6B]"}`}>
                      {totalByType[ct.id]}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Toolbar */}
          <div className="bg-white border border-[#E5E5E5] rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-sm font-bold text-[#181818]">{selectedTypeInfo?.label}</h2>
                <p className="text-xs text-[#706E6B]">{selectedTypeInfo?.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#706E6B]" />
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search..."
                    data-testid="ops-mdm-search"
                    className="pl-8 pr-3 h-8 text-sm border border-[#E5E5E5] rounded-lg w-48 focus:outline-none focus:border-[#0176D3]/50"
                  />
                </div>
                <button
                  onClick={() => setShowInactive(v => !v)}
                  className={`px-3 h-8 text-xs border rounded-lg transition-all ${showInactive ? "bg-[#F0FDF4] border-[#BBF7D0] text-[#166534]" : "border-[#E5E5E5] text-[#706E6B] hover:border-[#0176D3]/50"}`}
                  data-testid="ops-mdm-toggle-inactive"
                >
                  {showInactive ? "Showing All" : "Active Only"}
                </button>
                <button onClick={() => loadItems()} className="p-2 text-[#706E6B] hover:text-[#0176D3] border border-[#E5E5E5] rounded-lg" data-testid="ops-mdm-refresh">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={handleExport}
                  disabled={filteredItems.length === 0}
                  className="flex items-center gap-1.5 px-3 h-8 text-xs border border-[#E5E5E5] rounded-lg text-[#444444] hover:border-[#0176D3]/50 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="ops-mdm-export-btn"
                >
                  <Download className="w-3.5 h-3.5" />Export CSV
                </button>
                {canManage && (
                  <Button size="sm" onClick={() => { setShowCreate(true); setDetailItem(null); }} data-testid="ops-mdm-create-btn"
                    className="bg-[#0176D3] hover:bg-[#014486] text-white">
                    <Plus className="w-3.5 h-3.5 mr-1" />New
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Items grid + detail panel */}
          <div className="flex gap-4">
            {/* Item list */}
            <div className={`${detailItem || showCreate || editItem ? "w-72 flex-shrink-0" : "flex-1"}`}>
              {loading ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => <div key={i} className="h-16 bg-white border border-[#E5E5E5] rounded-xl animate-pulse" />)}
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] rounded-xl p-8 text-center text-sm text-[#706E6B]">
                  No {selectedTypeInfo?.label.toLowerCase()} configured.
                  {canManage && <span> Click <strong>New</strong> to create one.</span>}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredItems.map(item => {
                    const isSelected = detailItem?.id === item.id;
                    const isPlatform = item.tenant_id === "PLATFORM";
                    return (
                      <div
                        key={item.id}
                        data-testid={`ops-mdm-item-${item.item_code}`}
                        onClick={() => { setDetailItem(item); setHistoryData(null); setShowCreate(false); setEditItem(null); }}
                        className={`bg-white border rounded-xl p-4 cursor-pointer transition-all ${
                          isSelected ? "border-[#0176D3]/40 shadow-sm" : "border-[#E5E5E5] hover:border-[#0176D3]/30"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                              <span className="text-xs font-mono text-[#706E6B] bg-[#F3F3F3] px-1.5 py-0.5 rounded">
                                {item.item_code}
                              </span>
                              {isPlatform && <PlatformBadge />}
                              <StatusPill active={item.status === "ACTIVE"} />
                            </div>
                            <p className="text-sm font-semibold text-[#181818] truncate">{item.display_name}</p>
                            {item.description && (
                              <p className="text-xs text-[#706E6B] mt-0.5 line-clamp-2">{item.description}</p>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#706E6B] flex-shrink-0 mt-0.5" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Detail / Create / Edit panel */}
            {(detailItem || showCreate || editItem) && (
              <div className="flex-1 min-w-0">
                <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
                  {/* Create form */}
                  {showCreate && !editItem && (
                    <CreateEditForm
                      title={`New ${selectedTypeInfo?.label} Item`}
                      form={createForm}
                      onChange={setCreateForm}
                      onSubmit={handleCreate}
                      onCancel={() => setShowCreate(false)}
                      saving={saving}
                      selectedType={selectedType}
                    />
                  )}

                  {/* Edit form */}
                  {editItem && editForm && (
                    <CreateEditForm
                      title={`Edit: ${editItem.display_name}`}
                      form={editForm}
                      onChange={setEditForm}
                      onSubmit={handleEdit}
                      onCancel={() => setEditItem(null)}
                      saving={saving}
                      selectedType={selectedType}
                    />
                  )}

                  {/* Detail view */}
                  {detailItem && !showCreate && !editItem && (
                    <DetailPanel
                      item={detailItem}
                      historyData={historyData}
                      historyLoading={historyLoading}
                      canManage={canManage}
                      toggling={toggling}
                      onClose={() => { setDetailItem(null); setHistoryData(null); }}
                      onEdit={() => {
                        setEditItem(detailItem);
                        setEditForm({
                          display_name: detailItem.display_name,
                          description: detailItem.description || "",
                          sort_order: String(detailItem.sort_order || 99),
                          effective_from: detailItem.effective_from || "",
                          effective_to: detailItem.effective_to || "",
                          visibility_class: detailItem.visibility_class || "",
                          is_default: detailItem.is_default || false,
                          metadata_raw: JSON.stringify(detailItem.metadata || {}, null, 2),
                        });
                      }}
                      onToggle={() => handleToggle(detailItem)}
                      onHistory={() => loadHistory(detailItem)}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Detail Panel ──────────────────────────────────────────────────────────────

function DetailPanel({ item, historyData, historyLoading, canManage, toggling, onClose, onEdit, onToggle, onHistory }) {
  const isPlatform = item.tenant_id === "PLATFORM";
  const isActive = item.status === "ACTIVE";

  return (
    <div>
      <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
        <div>
          <span className="text-xs font-mono text-[#706E6B]">{item.item_code}</span>
          <h3 className="text-sm font-bold text-[#181818]">{item.display_name}</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {canManage && !isPlatform && (
            <button onClick={onEdit} className="p-1.5 text-[#706E6B] hover:text-[#0176D3] rounded-lg border border-[#E5E5E5] hover:border-[#0176D3]/50" data-testid="ops-mdm-edit-btn">
              <Edit3 className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={onHistory} className="p-1.5 text-[#706E6B] hover:text-[#5E4DB2] rounded-lg border border-[#E5E5E5] hover:border-[#5E4DB2]/50" data-testid="ops-mdm-history-btn">
            <History className="w-3.5 h-3.5" />
          </button>
          {canManage && !isPlatform && (
            <button
              onClick={onToggle}
              disabled={toggling === item.id}
              className={`p-1.5 rounded-lg border transition-all ${isActive ? "text-rose-500 border-rose-200 hover:border-rose-400" : "text-[#2E844A] border-[#BBF7D0] hover:border-[#2E844A]"}`}
              data-testid="ops-mdm-toggle-btn"
            >
              {isActive ? <ToggleLeft className="w-3.5 h-3.5" /> : <ToggleRight className="w-3.5 h-3.5" />}
            </button>
          )}
          <button onClick={onClose} className="p-1.5 text-[#706E6B] hover:text-[#181818] rounded-lg border border-[#E5E5E5]">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-3 text-sm">
        <div className="flex flex-wrap gap-2">
          <StatusPill active={isActive} />
          {isPlatform && <PlatformBadge />}
          {item.is_default && (
            <span className="text-[10px] font-mono bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">DEFAULT</span>
          )}
        </div>

        {item.description && <p className="text-[#444444] text-xs leading-relaxed">{item.description}</p>}

        <div className="grid grid-cols-2 gap-2 text-xs">
          {item.thread_type && <Field label="Thread Type" value={item.thread_type} />}
          {item.visibility_class && <Field label="Visibility" value={item.visibility_class} />}
          {item.sort_order != null && <Field label="Sort Order" value={item.sort_order} />}
          {item.effective_from && <Field label="Effective From" value={item.effective_from} />}
          {item.effective_to && <Field label="Effective To" value={item.effective_to} />}
        </div>

        {item.activation_triggers && Object.keys(item.activation_triggers).length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#706E6B] mb-1">Activation Triggers</p>
            <pre className="text-[10px] font-mono bg-[#F8FAFC] border border-[#E5E5E5] rounded-lg p-2 overflow-auto max-h-32 text-[#444444]">
              {JSON.stringify(item.activation_triggers, null, 2)}
            </pre>
          </div>
        )}

        {item.metadata && Object.keys(item.metadata).length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#706E6B] mb-1">Metadata</p>
            <pre className="text-[10px] font-mono bg-[#F8FAFC] border border-[#E5E5E5] rounded-lg p-2 overflow-auto max-h-40 text-[#444444]">
              {JSON.stringify(item.metadata, null, 2)}
            </pre>
          </div>
        )}

        {/* Revision History */}
        {historyLoading && (
          <div className="text-xs text-[#706E6B] flex items-center gap-1.5"><Clock className="w-3 h-3 animate-spin" /> Loading history…</div>
        )}
        {historyData && (
          <div>
            <p className="text-xs font-semibold text-[#706E6B] mb-2">Revision History ({historyData.total_revisions})</p>
            {historyData.revision_history?.length === 0 ? (
              <p className="text-xs text-[#706E6B]">No revisions recorded.</p>
            ) : (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {historyData.revision_history?.map((rev, i) => (
                  <div key={i} className="text-[10px] font-mono bg-[#F8FAFC] border border-[#E5E5E5] rounded p-2">
                    <span className="text-[#706E6B]">{rev.changed_at?.slice(0, 16)}</span>
                    {" · "}{rev.changed_by_email}
                    {" · "}<span className="text-[#0176D3]">{rev.changed_fields?.join(", ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <p className="text-[10px] text-[#706E6B] font-mono">
          ID: {item.id}
          {" · "}Created: {item.created_at?.slice(0, 10)}
          {item.created_by && ` · by ${item.created_by}`}
        </p>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-[#706E6B] font-mono uppercase tracking-wide">{label}</p>
      <p className="text-xs font-medium text-[#181818]">{String(value)}</p>
    </div>
  );
}

// ── Create / Edit Form ────────────────────────────────────────────────────────

function CreateEditForm({ title, form, onChange, onSubmit, onCancel, saving, selectedType }) {
  const set = (k, v) => onChange(prev => ({ ...prev, [k]: v }));
  const isThreadTemplate = selectedType === "THREAD_TEMPLATE";

  return (
    <div>
      <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
        <h3 className="text-sm font-bold text-[#181818]">{title}</h3>
        <button onClick={onCancel} className="p-1.5 text-[#706E6B] hover:text-[#181818] rounded-lg border border-[#E5E5E5]">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-[#444444]">Item Code *</Label>
            <Input
              placeholder="e.g. CUSTOMER_THREAD"
              value={form.item_code || ""}
              onChange={e => set("item_code", e.target.value)}
              className="mt-1 h-8 text-xs font-mono"
              data-testid="ops-mdm-form-item-code"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-[#444444]">Sort Order</Label>
            <Input
              type="number"
              placeholder="99"
              value={form.sort_order || ""}
              onChange={e => set("sort_order", e.target.value)}
              className="mt-1 h-8 text-xs"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs font-medium text-[#444444]">Display Name *</Label>
          <Input
            placeholder="Human-readable name"
            value={form.display_name || ""}
            onChange={e => set("display_name", e.target.value)}
            className="mt-1 h-8 text-sm"
            data-testid="ops-mdm-form-display-name"
          />
        </div>

        <div>
          <Label className="text-xs font-medium text-[#444444]">Description</Label>
          <Textarea
            placeholder="Purpose and usage notes"
            value={form.description || ""}
            onChange={e => set("description", e.target.value)}
            className="mt-1 text-xs resize-none"
            rows={2}
          />
        </div>

        {isThreadTemplate && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-[#444444]">Thread Type</Label>
              <Input
                placeholder="e.g. CUSTOMER"
                value={form.thread_type || ""}
                onChange={e => set("thread_type", e.target.value)}
                className="mt-1 h-8 text-xs font-mono"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#444444]">Visibility Class</Label>
              <select
                value={form.visibility_class || ""}
                onChange={e => set("visibility_class", e.target.value)}
                className="mt-1 w-full h-8 text-xs border border-[#E5E5E5] rounded-lg px-2 focus:outline-none focus:border-[#0176D3]/50"
              >
                <option value="">— select —</option>
                <option value="CUSTOMER_VISIBLE">CUSTOMER_VISIBLE</option>
                <option value="INTERNAL">INTERNAL</option>
                <option value="FINANCE_ONLY">FINANCE_ONLY</option>
                <option value="PARTNER">PARTNER</option>
                <option value="RESTRICTED">RESTRICTED</option>
              </select>
            </div>
          </div>
        )}

        {isThreadTemplate && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is-default-cb"
              checked={form.is_default || false}
              onChange={e => set("is_default", e.target.checked)}
              className="w-3.5 h-3.5"
            />
            <Label htmlFor="is-default-cb" className="text-xs text-[#444444] cursor-pointer">
              Is Default (activate automatically without explicit trigger)
            </Label>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-medium text-[#444444]">Effective From</Label>
            <Input type="date" value={form.effective_from || ""} onChange={e => set("effective_from", e.target.value)} className="mt-1 h-8 text-xs" />
          </div>
          <div>
            <Label className="text-xs font-medium text-[#444444]">Effective To</Label>
            <Input type="date" value={form.effective_to || ""} onChange={e => set("effective_to", e.target.value)} className="mt-1 h-8 text-xs" />
          </div>
        </div>

        <div>
          <Label className="text-xs font-medium text-[#444444]">Metadata (JSON)</Label>
          <Textarea
            placeholder='{"key": "value"}'
            value={form.metadata_raw || "{}"}
            onChange={e => set("metadata_raw", e.target.value)}
            className="mt-1 text-[10px] font-mono resize-none"
            rows={3}
            data-testid="ops-mdm-form-metadata"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={onSubmit} disabled={saving} className="bg-[#0176D3] hover:bg-[#014486] text-white" data-testid="ops-mdm-form-save">
            {saving ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null}
            Save
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
